import {
  S09CreateDraft,
  S09DesignDoc,
  S09Exits,
  S09Link,
  S09Meta,
  S09Step,
  apiTriggerType,
  catalogEntry,
  defaultExits,
  defaultMod,
  ensureModSteps,
  blankStepValues,
  isS09Branch,
} from "./s09-workflow.models";

let _seq = 0;
export function nextStepId(): string {
  _seq += 1;
  return `n_${Date.now().toString(36)}_${_seq}`;
}

/** Empty S-09 design — no seeded steps; user adds from the library. */
export function emptyDesign(meta?: Partial<S09Meta>, trigger = "status"): S09DesignDoc {
  const base: S09Meta = {
    code: meta?.code || "NEW",
    group: meta?.group || "patient",
    codes: meta?.codes || [],
    hub: meta?.hub || "",
    module: meta?.module ?? null,
    progress: meta?.progress || [],
    exhausted: meta?.exhausted || [],
    onExhausted: meta?.onExhausted || "",
    cancelTo: meta?.cancelTo || "",
    early: meta?.early || [],
    threshold: meta?.threshold || "",
    feed: meta?.feed || "",
    priority: meta?.priority || "Medium",
    caseType: meta?.caseType || "",
    caseTypeKey: meta?.caseTypeKey ?? null,
    caseTypeCode: meta?.caseTypeCode || "",
    guardrails: meta?.guardrails || [],
    openItems: meta?.openItems || [],
  };
  return {
    s09: 1,
    meta: base,
    trigger,
    triggerValue: base.threshold,
    triggerSource: base.feed,
    steps: [],
    links: [],
    exits: defaultExits(),
    layout: {
      trigger: { x: 120, y: 28, w: 240 },
    },
  };
}

export function designFromCreateDraft(d: S09CreateDraft): S09DesignDoc {
  const meta: S09Meta = {
    code: (d.code || "NEW").toUpperCase(),
    group: d.group || "patient",
    codes: [...(d.codes || [])],
    hub: d.hub || "",
    module: null,
    progress: [],
    exhausted: [],
    onExhausted: "",
    cancelTo: "",
    early: [],
    threshold: d.threshold,
    feed: d.feed,
    priority: d.priority,
    caseType: d.caseType,
    caseTypeKey: d.caseTypeKey ?? null,
    caseTypeCode: d.caseTypeCode || "",
    guardrails: [],
    openItems: [],
  };
  return emptyDesign(meta, d.trigger);
}

/** Copy a saved design and give the copy a new code. Steps, links, trigger, and case type stay. */
export function cloneDesign(source: S09DesignDoc, draft: S09CreateDraft): S09DesignDoc {
  const doc: S09DesignDoc = JSON.parse(JSON.stringify(source));
  doc.meta = {
    ...doc.meta,
    code: (draft.code || "NEW").toUpperCase(),
  };
  return doc;
}

export function parseDesignJson(raw: any): S09DesignDoc | null {
  if (!raw) return null;
  let obj = raw;
  if (typeof raw === "string") {
    try {
      obj = JSON.parse(raw);
    } catch {
      return null;
    }
  }
  if (obj?.s09 === 1 && obj?.meta && Array.isArray(obj?.steps)) {
    return ensureLinks(ensureModuleInnerSteps({
      s09: 1,
      meta: obj.meta,
      trigger: obj.trigger || "status",
      triggerValue: obj.triggerValue,
      triggerSource: obj.triggerSource,
      steps: (obj.steps || []).map((s: any) => ({
        id: s.id || nextStepId(),
        kind: s.kind,
        values: s.values || {},
        mod: s.mod,
        x: s.x,
        y: s.y,
        w: s.w,
        h: s.h,
      })),
      exits: obj.exits || defaultExits(),
      links: Array.isArray(obj.links) ? obj.links : undefined,
      layout: obj.layout || undefined,
    }));
  }
  // Legacy DESIGN_JSON — open as empty S-09 shell (no static conversion)
  if (Array.isArray(obj?.steps)) {
    return emptyDesign({ code: "NEW", caseType: "" }, "manual");
  }
  return null;
}

/** Shape expected by api/workflows/save-full `design` field. */
export function toSaveFullDesign(doc: S09DesignDoc): any {
  // Also mirror S-09 doc into trigger step config so older API builds that
  // strip unknown design roots still keep a recoverable copy.
  const s09Doc = JSON.parse(JSON.stringify(doc));
  return {
    version: 1,
    canvas: { scale: 1, pan_x: 60, pan_y: 60 },
    steps: [
      {
        step_key: "step_trigger",
        step_type_id:
          apiTriggerType(doc.trigger) === "schedule"
            ? "trigger_schedule"
            : apiTriggerType(doc.trigger) === "manual"
              ? "trigger_manual"
              : "trigger_event",
        step_label: "Trigger",
        canvas_x: 80,
        canvas_y: 40,
        step_order: 0,
        config: {
          s09_trigger: doc.trigger,
          event_type_key: doc.triggerSource || "",
          frequency: doc.trigger === "schedule" ? "Daily" : undefined,
          s09_doc: s09Doc,
        },
      },
    ],
    connections: [],
    s09: s09Doc,
  };
}

export function fromLoadFull(payload: any): S09DesignDoc | null {
  const design = payload?.design ?? payload?.data?.design ?? payload;
  if (design?.s09?.s09 === 1) return parseDesignJson(design.s09);
  if (design?.s09 === 1) return parseDesignJson(design);
  // Fallback: recover from trigger step config.s09_doc (pre-s09-root API)
  const steps = design?.steps;
  if (Array.isArray(steps)) {
    const trig = steps.find(
      (s: any) => s?.config?.s09_doc?.s09 === 1 || s?.step_key === "step_trigger"
    );
    const embedded = trig?.config?.s09_doc;
    if (embedded?.s09 === 1) return parseDesignJson(embedded);
  }
  return parseDesignJson(design);
}

export function wfDisplayName(brand: string, meta: S09Meta, name: string): string {
  const codePart = meta.code && meta.code !== "MOD" ? meta.code + " " : "";
  return `${brand} · ${codePart}${name}`.replace(/\s+/g, " ").trim();
}

export function defaultStepSize(kind: string): { w: number; h?: number } {
  if (kind === "outreachModule") return { w: 340 };
  if (kind === "draftEmail" || kind === "frmAction" || kind === "frmDecision" || kind === "assignCase") return { w: 320 };
  if (kind === "attempt") return { w: 260 };
  return { w: 220 };
}

export function placeNewStep(
  doc: S09DesignDoc,
  kind: string
): { x: number; y: number; w: number } {
  const { w } = defaultStepSize(kind);
  const idx = doc.steps.length;
  const trig = doc.layout?.trigger || { x: 120, y: 28, w: 240 };
  // Cascade below the last positioned step, or under the trigger.
  let y = (trig.y || 28) + 72;
  let x = trig.x || 120;
  if (doc.steps.length) {
    const last = doc.steps[doc.steps.length - 1];
    x = last.x ?? x;
    y = (last.y ?? y) + (last.h ?? 96) + 36;
  } else {
    y = (trig.y || 28) + 80;
    x = Math.max(24, (trig.x || 120) + ((trig.w || 240) - w) / 2);
  }
  // Mild stagger so stacked adds don't perfectly overlap.
  x += (idx % 4) * 8;
  return { x: Math.max(16, x), y: Math.max(16, y), w };
}

/** Seed FRM Action on outreach modules saved before inner steps existed. */
export function ensureModuleInnerSteps(doc: S09DesignDoc): S09DesignDoc {
  let changed = false;
  const steps = doc.steps.map((s) => {
    if (String(s.kind) !== "outreachModule") return s;
    if (s.mod && Array.isArray(s.mod.steps)) {
      const needsNorm =
        s.mod.steps.some(
          (ms) => !ms?.id || (ms.kind === "frmAction" && !Array.isArray(ms.values?.["days"]))
        ) || (s.mod.attempts || []).some((a) => !Array.isArray(a?.after));
      if (!needsNorm) return s;
      changed = true;
      return { ...s, mod: ensureModSteps(s.mod) };
    }
    changed = true;
    return { ...s, mod: ensureModSteps(s.mod) };
  });
  if (!changed) return doc;
  return { ...doc, steps };
}

export function ensureCanvasLayout(doc: S09DesignDoc): S09DesignDoc {
  const layout = {
    trigger: doc.layout?.trigger || { x: 120, y: 28, w: 240 },
  };
  // Compact legacy default widths from the earlier larger canvas.
  if (layout.trigger.w === 320) layout.trigger = { ...layout.trigger, w: 240 };
  const steps = doc.steps.map((s, i) => {
    let w = s.w;
    if (w == null) {
      w = placeNewStep({ ...doc, steps: doc.steps.slice(0, i) }, String(s.kind)).w;
    } else if (String(s.kind) === "outreachModule" && w === 480) {
      w = 340;
    } else if (w === 280) {
      w = 220;
    }
    if (s.x != null && s.y != null && s.w != null && w === s.w) return s;
    const placed = placeNewStep({ ...doc, steps: doc.steps.slice(0, i) }, String(s.kind));
    return {
      ...s,
      x: s.x ?? placed.x,
      y: s.y ?? placed.y,
      w: w ?? placed.w,
    };
  });
  return ensureLinks({ ...doc, steps, layout });
}

/** Straight chain used when a saved workflow has no explicit connections. */
export function linearLinks(doc: S09DesignDoc): S09Link[] {
  const steps = doc.steps || [];
  if (!steps.length) return [];
  const links: S09Link[] = [{ from: "trigger", to: steps[0].id }];
  for (let i = 0; i < steps.length - 1; i++) {
    links.push({ from: steps[i].id, to: steps[i + 1].id });
  }
  return links;
}

export function ensureLinks(doc: S09DesignDoc): S09DesignDoc {
  const ids = new Set(doc.steps.map((s) => s.id));
  const raw = Array.isArray(doc.links) ? doc.links : linearLinks(doc);
  const seen = new Set<string>();
  const links: S09Link[] = [];
  for (const link of raw) {
    const from = String(link?.from || "");
    const to = String(link?.to || "");
    if (!from || !to || from === to) continue;
    if (from !== "trigger" && !ids.has(from)) continue;
    if (!ids.has(to)) continue;
    const branch = isS09Branch(link?.branch) ? link.branch : undefined;
    const key = `${from}>${to}>${branch || ""}`;
    if (seen.has(key)) continue;
    seen.add(key);
    const next: S09Link = { from, to };
    if (branch) next.branch = branch;
    if (typeof link.bendX === "number") next.bendX = link.bendX;
    if (typeof link.bendY === "number") next.bendY = link.bendY;
    if (link.toSide === "left" || link.toSide === "right") next.toSide = link.toSide;
    if (link.fromSide === "left" || link.fromSide === "right") next.fromSide = link.fromSide;
    links.push(next);
  }
  const cur = doc.links || [];
  if (
    Array.isArray(doc.links) &&
    cur.length === links.length &&
    cur.every(
      (l, i) =>
        l.from === links[i].from &&
        l.to === links[i].to &&
        (l.branch || "") === (links[i].branch || "") &&
        (l.bendX ?? null) === (links[i].bendX ?? null) &&
        (l.bendY ?? null) === (links[i].bendY ?? null) &&
        (l.toSide || "") === (links[i].toSide || "") &&
        (l.fromSide || "") === (links[i].fromSide || "")
    )
  ) {
    return doc;
  }
  return { ...doc, links };
}

/** Attach a new step after the last step that does not already continue. */
export function withTailLink(doc: S09DesignDoc, newId: string): S09DesignDoc {
  const base = ensureLinks(doc);
  const links = [...(base.links || [])];
  const others = base.steps.filter((s) => s.id !== newId);
  let from = "trigger";
  if (others.length) {
    const hasOut = new Set(links.map((l) => l.from));
    const open = others.filter((s) => !hasOut.has(s.id));
    from = (open.length ? open[open.length - 1] : others[others.length - 1]).id;
  }
  if (!links.some((l) => l.from === from && l.to === newId)) {
    links.push({ from, to: newId });
  }
  return { ...base, links };
}

function nodeBox(doc: S09DesignDoc, id: string): { x: number; y: number; w: number; h: number } {
  if (id === "trigger") {
    const t = doc.layout?.trigger || { x: 120, y: 28, w: 240 };
    return { x: t.x || 0, y: t.y || 0, w: t.w || 240, h: 48 };
  }
  const step = doc.steps.find((s) => s.id === id);
  return { x: step?.x || 0, y: step?.y || 0, w: step?.w || 220, h: step?.h || 96 };
}

/** Put a new step on an existing connection and rewire that connection through it. */
export function insertStepBetween(
  doc: S09DesignDoc,
  kind: string,
  from: string,
  to: string
): S09DesignDoc {
  const before = new Set(doc.steps.map((s) => s.id));
  const addedDoc = kind === "attempt" ? addAttemptSteps(doc) : addLibraryStep(doc, kind);
  const added = addedDoc.steps.find((s) => !before.has(s.id));
  if (!added) return doc;
  const kept = (addedDoc.links || []).filter((l) => l.to !== added.id);
  const old = kept.find((l) => l.from === from && l.to === to);
  const spliced = kept.filter((l) => !(l.from === from && l.to === to));
  spliced.push(
    old?.branch ? { from, to: added.id, branch: old.branch } : { from, to: added.id },
    { from: added.id, to }
  );
  const above = nodeBox(addedDoc, from);
  const below = nodeBox(addedDoc, to);
  const gap = below.y - (above.y + above.h);
  const x = gap < 140 ? Math.max(above.x + above.w, below.x + below.w) + 28 : above.x;
  const y = gap < 140 ? Math.round((above.y + below.y) / 2) : above.y + above.h + 28;
  return {
    ...addedDoc,
    links: spliced,
    steps: addedDoc.steps.map((s) => (s.id === added.id ? { ...s, x, y } : s)),
  };
}

/** One named attempt on the canvas. The name can be Attempt 1 or Day 1 Attempt. */
export function addAttemptSteps(doc: S09DesignDoc): S09DesignDoc {
  const attemptNo =
    doc.steps.reduce((max, step) => Math.max(max, Number(step.values?.["attemptNo"]) || 0), 0) + 1;
  const values = blankStepValues("attempt");
  values["name"] = `Attempt ${attemptNo}`;
  values["attemptNo"] = attemptNo;
  values["runsAfter"] =
    attemptNo === 1 ? "After 1 day" : attemptNo === 2 ? "After 2 days" : "After 3 days";
  values["onOutcome"] = "next";
  const geom = placeNewStep(doc, "attempt");
  const step: S09Step = {
    id: nextStepId(),
    kind: "attempt",
    values,
    x: geom.x,
    y: geom.y,
    w: geom.w,
  };
  const laid = ensureCanvasLayout({ ...doc, steps: [...doc.steps, step] });
  return withTailLink(laid, step.id);
}

export function addLibraryStep(doc: S09DesignDoc, kind: string): S09DesignDoc {
  const cat = catalogEntry(kind);
  if (!cat || cat.future) return doc;
  if (kind === "attempt") return addAttemptSteps(doc);
  const values = blankStepValues(kind);
  const geom = placeNewStep(doc, kind);
  const s: S09Step = {
    id: nextStepId(),
    kind,
    values,
    x: geom.x,
    y: geom.y,
    w: geom.w,
  };
  if (kind === "outreachModule") s.mod = defaultMod();
  const next = ensureCanvasLayout({ ...doc, steps: [...doc.steps, s] });
  return withTailLink(next, s.id);
}

export function exitsFromMeta(meta: S09Meta): S09Exits {
  return {
    progress: [...(meta.progress || [])],
    exhausted: [...(meta.exhausted || [])],
    onExhausted: meta.onExhausted || "",
    cancelTo: meta.cancelTo || "",
    early: [...(meta.early || [])],
  };
}
